import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { StepperFormFieldsContainer } from '../../../components/Form/FormComponents';
import { FormFactoryWithStandardLayout } from '../../../components/Form/FormFactory';
import { useInvestmentFields } from '../../../data-fields/InvestmentFields';
import { IFinanceRoundDataModel } from '../../../data-models/finance-round.data-model';
import { ITransactionDataModel } from '../../../data-models/transaction.data-model';
import { formatISODateOnly } from '../../../util/formatters/DateFormatters';
import { getRoundLabel } from '../../../util/preference-utils';
import { captureFormValidationError } from '../../../util/SentryUtils';
import {
  createFinanceRoundFormViewModel,
  fromFinanceRoundDataModel,
  IFinanceRoundFormViewModel,
  isNewFinanceRound,
  isNewRoundId,
  toFinanceRoundDataModel,
} from '../../../view-models/finance-round-form.view-model';
import { createFormFromFieldsArray, IForm } from '../../../view-models/form.view-model';
import { useInvestmentActions } from '../hooks/useInvestmentActions';
import { companyRoundsByIdStateJ } from '../state/FinanceStateJ';
import { assignToInvestmentSchema } from '../Transactions/AssignToRound/investmentSchemas';
import { InvestmentSelectForm } from './InvestmentSelectForm';
import { StepperFormButtons } from './StepperFormButtons';
import {
  currentStepStateFinanceJ,
  investmentDataState,
  selectedCompanyIdState,
} from './TransactionFormUIStateJ';

export interface IInvestmentFormProps {
  companyId: number;
  formConfig?: Omit<IForm, 'fields'>;
  removeConfig?: {
    transaction: ITransactionDataModel;
    onRemove: () => void;
  };
}

export function InvestmentCreateForm(props: Omit<IInvestmentFormProps, 'companyId'>) {
  const { formConfig } = props;
  const { variant = 'form' } = formConfig ?? {};
  const title = formConfig?.title ?? `Enter New ${getRoundLabel()} Information`;
  const investmentFields = useInvestmentFields();

  const form = createFormFromFieldsArray(investmentFields, {
    title,
    variant,
  });

  return <FormFactoryWithStandardLayout form={form} />;
}

export function InvestmentSelectOrCreateForm(props: IInvestmentFormProps) {
  const { setValue } = useFormContext<Partial<IFinanceRoundFormViewModel>>();
  const { companyId, removeConfig } = props;
  const [showNewInvestmentForm, setShowNewInvestmentForm] = useState(false);

  const onAddNewInvestment = useCallback(() => {
    setValue('id', undefined);
    setShowNewInvestmentForm(true);
  }, [setValue]);

  return (
    <Stack display={'grid'} gridTemplateRows={'min-content 1fr'} gap='1rem'>
      {showNewInvestmentForm ? (
        <InvestmentCreateForm />
      ) : (
        <InvestmentSelectForm
          companyId={companyId}
          onAddNewInvestment={onAddNewInvestment}
          removeConfig={removeConfig}
        />
      )}
    </Stack>
  );
}

export function InvestmentFormStep() {
  const companyId = useAtomValue(selectedCompanyIdState);
  const [investmentData, setInvestmentData] = useAtom(investmentDataState);
  const investmentsForCompany = useAtomValue(companyRoundsByIdStateJ(companyId ?? -1));
  const { handleCreateInvestment } = useInvestmentActions();
  const setCurrentStep = useSetAtom(currentStepStateFinanceJ);

  const defaultValues = useMemo(() => {
    return investmentData
      ? fromFinanceRoundDataModel(investmentData as IFinanceRoundDataModel)
      : getInitialData(companyId!);
  }, [companyId, investmentData]);

  const methods = useForm<IFinanceRoundFormViewModel>({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(assignToInvestmentSchema),
  });

  const {
    getValues,
    trigger,
    reset,
    control,
    formState: { errors },
  } = methods;
  const [showNewInvestmentForm, setShowNewInvestmentForm] = useState(
    () => investmentData !== null && isNewFinanceRound(investmentData)
  );

  const investmentRoundId = useWatch({ name: 'id', control });
  const isSkippingRound = !showNewInvestmentForm && isNewRoundId(investmentRoundId);
  const nextButtonLabel = isSkippingRound ? 'Skip' : 'Next';

  const onAddNewInvestment = useCallback(
    (name?: string) => {
      reset({ ...getInitialData(companyId!), name });
      setShowNewInvestmentForm(true);
    },
    [companyId, reset]
  );

  const handleNextStep = useCallback(async () => {
    const data = getValues();
    const financeRoundDataModel = toFinanceRoundDataModel(data);
    const isNew = !isSkippingRound && isNewFinanceRound(data);

    if (isSkippingRound) {
      setInvestmentData(null);
      return true;
    }

    if (!isNew) {
      setInvestmentData(investmentsForCompany.get(data.id ?? -1) ?? financeRoundDataModel);
      return true;
    }

    const isNewInvestmentValid = await trigger();
    if (isNewInvestmentValid) {
      const res = await handleCreateInvestment(financeRoundDataModel);

      if (res) {
        setInvestmentData(res);
        return true;
      }
    } else {
      captureFormValidationError('Create investment', errors);
    }

    return false;
  }, [
    getValues,
    handleCreateInvestment,
    investmentsForCompany,
    isSkippingRound,
    setInvestmentData,
    trigger,
    errors,
  ]);

  const handleGoBack = useCallback(() => {
    if (showNewInvestmentForm) {
      reset(getInitialData(companyId!));
      setShowNewInvestmentForm(false);
      return;
    }

    const formData = getValues();

    if (isSkippingRound) {
      setInvestmentData(null);
    } else {
      setInvestmentData(formData);
    }

    setCurrentStep((prev) => prev - 1);
  }, [
    companyId,
    getValues,
    isSkippingRound,
    reset,
    setCurrentStep,
    setInvestmentData,
    showNewInvestmentForm,
  ]);

  if (companyId === null) {
    return <div>No company selected</div>;
  }

  return (
    <FormProvider {...methods}>
      <StepperFormFieldsContainer style={{ alignContent: 'start' }}>
        {showNewInvestmentForm ? (
          <InvestmentCreateForm />
        ) : (
          <>
            <Typography variant='body1'>{`Select a ${getRoundLabel()}`}</Typography>
            <InvestmentSelectForm companyId={companyId} onAddNewInvestment={onAddNewInvestment} />
          </>
        )}
      </StepperFormFieldsContainer>
      <StepperFormButtons
        stepIsValid={handleNextStep}
        nextButtonLabel={nextButtonLabel}
        handleGoBack={handleGoBack}
        backButtonLabel={showNewInvestmentForm ? 'Cancel' : undefined}
      />
    </FormProvider>
  );
}

function getInitialData(companyId: number) {
  return createFinanceRoundFormViewModel({
    companyId,
    roundDate: formatISODateOnly(new Date()),
  });
}
